import { LegacyBlock, LegacyPageContent } from './legacy';

// ---------------------------------------------------------------------------
// Universal CMS page model (content_json v2)
// PageContent -> sections -> columns -> blocks. LegacyPageContent (v1) is
// wrapped into a single default section on load.
// ---------------------------------------------------------------------------

export type PageTheme = {
  backgroundColor?: string;
  surfaceColor?: string;
  textColor?: string;
  accentColor?: string;
  fontFamily?: string;
};

export type PageSectionSettings = {
  backgroundColor?: string;
  textColor?: string;
  paddingY?: number | string;
  paddingTop?: number | string;
  paddingBottom?: number | string;
  container?: 'boxed' | 'full';
  anchor?: string;
};

export type PageBlockStyle = {
  width?: number; // percent 1..100 within its column
  alignment?: 'LEFT' | 'CENTER' | 'RIGHT';
  backgroundColor?: string;
  textColor?: string;
  animation?: 'none' | 'fade-in' | 'fade-up' | 'fade-left' | 'scale-in';
  animationDelay?: number;
};

export type PageBlock = {
  id: string;
  type: string;
  enabled?: boolean;
  adminLabel?: string;
  props: Record<string, any>;
  style?: PageBlockStyle;
};

export type PageColumn = {
  id: string;
  width?: number; // percent 1..100 within the section row
  blocks: PageBlock[];
};

export type PageSection = {
  id: string;
  settings?: PageSectionSettings;
  columns: PageColumn[];
};

export type PageContentV2 = {
  version: 2;
  theme?: PageTheme;
  sections: PageSection[];
  /**
   * Preserved top-level fields when a legacy v1 document is loaded, so the
   * editor can round-trip back to the v1 format on save (legacy public
   * endpoints parse content_json as LegacyPageContent).
   */
  legacy?: { title?: string; event?: string; eventDate?: string; article?: string; links?: { label: string; url: string }[]; sourceUrl?: string };
};

export const uid = () => globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;

// ---------------------------------------------------------------------------
// Block registry: label + default props + settings field schema.
// `fields` drive the generic settings form in the editor.
// ---------------------------------------------------------------------------

export type BlockFieldType =
  | 'text' | 'textarea' | 'markdown' | 'number' | 'select' | 'color'
  | 'toggle' | 'image' | 'links' | 'images' | 'faqs' | 'prices' | 'testimonials' | 'slides';

export type BlockFieldDef = {
  key: string;
  label: string;
  type: BlockFieldType;
  options?: { value: string; label: string }[];
};

export type BlockDef = {
  type: string;
  label: string;
  defaults: Record<string, any>;
  fields: BlockFieldDef[];
};

const alignField: BlockFieldDef = {
  key: 'align', label: 'Zarovnání', type: 'select',
  options: [
    { value: 'left', label: 'Vlevo' },
    { value: 'center', label: 'Na střed' },
    { value: 'right', label: 'Vpravo' },
  ],
};

export const blockDefs: Record<string, BlockDef> = {
  HEADING: {
    type: 'HEADING', label: 'Nadpis',
    defaults: { text: 'Nadpis', level: 'h2', align: 'left' },
    fields: [
      { key: 'text', label: 'Text', type: 'text' },
      { key: 'level', label: 'Úroveň', type: 'select', options: [
        { value: 'h1', label: 'H1' }, { value: 'h2', label: 'H2' },
        { value: 'h3', label: 'H3' }, { value: 'h4', label: 'H4' }] },
      alignField,
    ],
  },
  TEXT: {
    type: 'TEXT', label: 'Text',
    defaults: { title: '', content: '' },
    fields: [
      { key: 'title', label: 'Nadpis (nepovinný)', type: 'text' },
      { key: 'content', label: 'Text (markdown)', type: 'markdown' },
    ],
  },
  HERO: {
    type: 'HERO', label: 'Úvodní blok',
    defaults: { title: '', content: '', imageUrl: '', imageAlt: '', linkUrl: '', linkLabel: '', align: 'center' },
    fields: [
      { key: 'title', label: 'Titulek', type: 'text' },
      { key: 'content', label: 'Text', type: 'markdown' },
      { key: 'imageUrl', label: 'Obrázek', type: 'image' },
      { key: 'imageAlt', label: 'Alt text', type: 'text' },
      { key: 'height', label: 'Výška (px)', type: 'number' },
      { key: 'linkUrl', label: 'URL tlačítka', type: 'text' },
      { key: 'linkLabel', label: 'Popisek tlačítka', type: 'text' },
      alignField,
    ],
  },
  SLIDER: {
    type: 'SLIDER', label: 'Slider',
    defaults: { height: 0, slides: [{ title: '', content: '', imageUrl: '', linkUrl: '', linkLabel: '' }] },
    fields: [
      { key: 'slides', label: 'Snímky', type: 'slides' },
      { key: 'height', label: 'Výška (px)', type: 'number' },
    ],
  },
  MARKDOWN: {
    type: 'MARKDOWN', label: 'Text (markdown)',
    defaults: { title: '', content: '' },
    fields: [
      { key: 'title', label: 'Nadpis (nepovinný)', type: 'text' },
      { key: 'content', label: 'Text (markdown)', type: 'markdown' },
    ],
  },
  IMAGE: {
    type: 'IMAGE', label: 'Obrázek',
    defaults: { imageUrl: '', imageAlt: '', caption: '', linkUrl: '' },
    fields: [
      { key: 'imageUrl', label: 'Obrázek', type: 'image' },
      { key: 'imageAlt', label: 'Alt text', type: 'text' },
      { key: 'caption', label: 'Popisek', type: 'text' },
      { key: 'linkUrl', label: 'Odkaz', type: 'text' },
    ],
  },
  GALLERY: {
    type: 'GALLERY', label: 'Galerie',
    defaults: { title: '', images: [{ url: '', alt: '' }] },
    fields: [
      { key: 'title', label: 'Nadpis', type: 'text' },
      { key: 'images', label: 'Obrázky', type: 'images' },
    ],
  },
  VIDEO: {
    type: 'VIDEO', label: 'Video',
    defaults: { url: '', title: '' },
    fields: [
      { key: 'url', label: 'URL videa (YouTube / embed)', type: 'text' },
      { key: 'title', label: 'Titulek', type: 'text' },
    ],
  },
  BUTTON: {
    type: 'BUTTON', label: 'Tlačítko',
    defaults: { label: 'Tlačítko', url: '', align: 'center' },
    fields: [
      { key: 'label', label: 'Popisek', type: 'text' },
      { key: 'url', label: 'URL', type: 'text' },
      alignField,
    ],
  },
  CTA: {
    type: 'CTA', label: 'Výzva k akci',
    defaults: { title: '', content: '', linkUrl: '', linkLabel: '' },
    fields: [
      { key: 'title', label: 'Nadpis', type: 'text' },
      { key: 'content', label: 'Text', type: 'markdown' },
      { key: 'linkUrl', label: 'URL tlačítka', type: 'text' },
      { key: 'linkLabel', label: 'Popisek tlačítka', type: 'text' },
    ],
  },
  LINKS: {
    type: 'LINKS', label: 'Odkazy',
    defaults: { title: '', links: [{ label: '', url: '' }], inline: false },
    fields: [
      { key: 'title', label: 'Nadpis', type: 'text' },
      { key: 'links', label: 'Odkazy', type: 'links' },
      { key: 'inline', label: 'Vodorovně (navigace)', type: 'toggle' },
    ],
  },
  BOOKING: {
    type: 'BOOKING', label: 'Rezervace',
    defaults: { title: '', content: '', serviceSlug: '' },
    fields: [
      { key: 'title', label: 'Nadpis', type: 'text' },
      { key: 'content', label: 'Text', type: 'markdown' },
      { key: 'serviceSlug', label: 'Slugy rezervačních služeb (čárkou)', type: 'text' },
    ],
  },
  FAQ: {
    type: 'FAQ', label: 'Časté otázky',
    defaults: { title: 'Časté otázky', items: [{ question: '', answer: '' }] },
    fields: [
      { key: 'title', label: 'Nadpis', type: 'text' },
      { key: 'items', label: 'Otázky', type: 'faqs' },
    ],
  },
  PRICELIST: {
    type: 'PRICELIST', label: 'Ceník',
    defaults: { title: 'Ceník', items: [{ name: '', price: '', description: '', featured: false }] },
    fields: [
      { key: 'title', label: 'Nadpis', type: 'text' },
      { key: 'items', label: 'Položky', type: 'prices' },
    ],
  },
  TESTIMONIALS: {
    type: 'TESTIMONIALS', label: 'Reference',
    defaults: { title: 'Reference', items: [{ quote: '', author: '', role: '' }] },
    fields: [
      { key: 'title', label: 'Nadpis', type: 'text' },
      { key: 'items', label: 'Reference', type: 'testimonials' },
    ],
  },
  MAP: {
    type: 'MAP', label: 'Mapa',
    defaults: { title: '', embedUrl: '', address: '' },
    fields: [
      { key: 'title', label: 'Nadpis', type: 'text' },
      { key: 'embedUrl', label: 'Embed URL mapy', type: 'text' },
      { key: 'address', label: 'Adresa', type: 'text' },
    ],
  },
  DIVIDER: {
    type: 'DIVIDER', label: 'Oddělovač',
    defaults: { style: 'line' },
    fields: [{ key: 'style', label: 'Styl', type: 'select', options: [
      { value: 'line', label: 'Čára' }, { value: 'ornament', label: 'Ornament' }, { value: 'space', label: 'Jen mezera' }] }],
  },
  SPACER: {
    type: 'SPACER', label: 'Mezera',
    defaults: { height: 48 },
    fields: [{ key: 'height', label: 'Výška (px)', type: 'number' }],
  },
  EMBED: {
    type: 'EMBED', label: 'Vlastní HTML',
    defaults: { html: '' },
    fields: [{ key: 'html', label: 'HTML kód', type: 'textarea' }],
  },
};

export const blockTypeList = Object.keys(blockDefs);

export function createPageBlock(type: string): PageBlock {
  const def = blockDefs[type];
  const props = def ? structuredClone(def.defaults) : {};
  return { id: uid(), type, enabled: true, props, style: { width: 100, alignment: 'LEFT' } };
}

export function createPageColumn(): PageColumn {
  return { id: uid(), blocks: [] };
}

export function createPageSection(columnCount = 1, id?: string): PageSection {
  const width = Math.floor(100 / columnCount);
  const columns = Array.from({ length: columnCount }, () => ({ ...createPageColumn(), width }));
  return { id: id ?? uid(), settings: {}, columns };
}

export function emptyPageContent(theme?: PageTheme): PageContentV2 {
  return {
    version: 2,
    theme: theme ?? {},
    sections: [{ id: 'sec-0', settings: {}, columns: [{ id: 'col-0-0', width: 100, blocks: [] }] }],
  };
}

// ---------------------------------------------------------------------------
// Adapter: normalize any stored JSON to PageContentV2.
// ---------------------------------------------------------------------------

const legacyWidthMap: Record<string, number> = { FULL: 100, HALF: 50, THIRD: 33, TWO_THIRDS: 66 };

export function toPageBlock(block: any, fallbackId?: string): PageBlock {
  const { id, type, enabled, width, alignment, adminLabel, backgroundColor, textColor, animation, animationDelay, condition, props, style, ...rest } = block || {};
  // `props`/`style` are v2-native fields — merge them instead of re-nesting
  const v2Props = props && typeof props === 'object' && !Array.isArray(props) ? props : undefined;
  const v2Style = style && typeof style === 'object' && !Array.isArray(style) ? style : undefined;
  return {
    id: typeof id === 'string' && id ? id : (fallbackId ?? uid()),
    type: type === 'MARKDOWN' ? 'TEXT' : (type || 'TEXT'),
    enabled: enabled !== false,
    adminLabel,
    props: { ...(v2Props ?? {}), ...rest },
    style: {
      ...(v2Style ?? {}),
      width: legacyWidthMap[width] ?? (typeof width === 'number' ? width : (v2Style?.width ?? 100)),
      alignment: alignment === 'CENTER' || alignment === 'RIGHT' ? alignment : (v2Style?.alignment ?? 'LEFT'),
      ...(backgroundColor || v2Style?.backgroundColor ? { backgroundColor: backgroundColor ?? v2Style?.backgroundColor } : {}),
      ...(textColor || v2Style?.textColor ? { textColor: textColor ?? v2Style?.textColor } : {}),
      ...(animation || v2Style?.animation ? { animation: animation ?? v2Style?.animation, animationDelay: animationDelay ?? v2Style?.animationDelay } : {}),
    },
  };
}

export function toPageContent(input: any): PageContentV2 {
  if (input && Array.isArray(input.sections)) {
    const theme = input.theme ?? {};
    const sections = input.sections.map((s: any, si: number) => ({
      id: s.id || `sec-${si}`,
      settings: s.settings ?? {},
      columns: (Array.isArray(s.columns) && s.columns.length ? s.columns : [createPageColumn()]).map((c: any, ci: number) => ({
        id: c.id || `col-${si}-${ci}`,
        width: typeof c.width === 'number' ? c.width : undefined,
        blocks: (c.blocks ?? []).map((b: any, bi: number) => toPageBlock(b, `blk-${si}-${ci}-${bi}`)),
      })),
    }));
    return { version: 2, theme, sections, ...(input.legacy ? { legacy: input.legacy } : {}) };
  }
  // Legacy LegacyPageContent (v1): wrap flat blocks into one section / one column
  // and keep the top-level fields for a lossless round-trip on save.
  const theme = input?.theme ?? {};
  const blocks = (input?.blocks ?? []).map((b: any, bi: number) => toPageBlock(b, `blk-0-0-${bi}`));
  const legacy = input && typeof input === 'object' && !Array.isArray(input) ? {
    title: input.title, event: input.event, eventDate: input.eventDate,
    article: input.article, links: input.links, sourceUrl: input.sourceUrl,
  } : undefined;
  return {
    version: 2,
    theme,
    legacy,
    sections: [{ id: 'sec-0', settings: {}, columns: [{ id: 'col-0-0', width: 100, blocks }] }],
  };
}

function pctToLegacyWidth(pct?: number): string {
  if (pct == null || pct >= 100) return 'FULL';
  if (pct <= 40) return 'THIRD';
  if (pct <= 60) return 'HALF';
  return 'TWO_THIRDS';
}

/**
 * Convert universal content back to the legacy LegacyPageContent shape so that
 * pages which were stored as v1 keep working with the old public endpoints.
 */
export function fromPageContentToLegacy(content: PageContentV2): any {
  const blocks = content.sections
    .flatMap(s => s.columns.flatMap(c => c.blocks))
    .map(b => ({
      id: b.id,
      type: b.type === 'TEXT' ? 'MARKDOWN' : b.type,
      enabled: b.enabled !== false,
      width: pctToLegacyWidth(b.style?.width),
      alignment: b.style?.alignment ?? 'LEFT',
      ...(b.adminLabel ? { adminLabel: b.adminLabel } : {}),
      ...(b.style?.backgroundColor ? { backgroundColor: b.style.backgroundColor } : {}),
      ...(b.style?.textColor ? { textColor: b.style.textColor } : {}),
      ...(b.style?.animation ? { animation: b.style.animation, animationDelay: b.style?.animationDelay } : {}),
      ...b.props,
    }));
  const legacy = content.legacy ?? {};
  return {
    title: legacy.title ?? '',
    event: legacy.event ?? '',
    eventDate: legacy.eventDate ?? '',
    article: legacy.article ?? '',
    links: legacy.links ?? [],
    sourceUrl: legacy.sourceUrl ?? '',
    theme: content.theme,
    blocks,
  };
}

export function isLegacyContent(input: unknown): boolean {
  return !!input && typeof input === 'object' && !Array.isArray((input as any).sections) && !Array.isArray(input);
}

export function validatePageContent(input: unknown): { valid: boolean; error?: string } {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    return { valid: false, error: 'Content must be an object' };
  }
  const sections = (input as PageContentV2).sections;
  if (!Array.isArray(sections)) {
    return { valid: false, error: 'Missing sections array' };
  }
  for (let si = 0; si < sections.length; si++) {
    const cols = sections[si]?.columns;
    if (!Array.isArray(cols) || cols.length === 0) {
      return { valid: false, error: `section[${si}]: missing columns` };
    }
    for (let ci = 0; ci < cols.length; ci++) {
      const blocks = cols[ci]?.blocks;
      if (!Array.isArray(blocks)) {
        return { valid: false, error: `section[${si}].columns[${ci}]: missing blocks` };
      }
      for (const b of blocks) {
        if (!b?.id || typeof b.id !== 'string') return { valid: false, error: 'block missing id' };
        if (!b?.type || !(b.type in blockDefs)) return { valid: false, error: `unknown block type: ${b?.type}` };
      }
    }
  }
  return { valid: true };
}
